// Dashboard Service
// Este servicio maneja las estadísticas del dashboard del profesor

import ApiService from './ApiService';

class DashboardService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://localhost:7156/api';
  }

  // Obtener estadísticas del dashboard del profesor
  async getEstadisticas() {
    try {
      
      // Debug del estado del token antes de hacer la petición
      ApiService.debugTokenStatus();
      
      const response = await ApiService.get('/DashboardProfesor/estadisticas');
      
      // Log de la respuesta recibida
      
      return response;
      
    } catch (error) {
      console.error('❌ Error al obtener estadísticas del dashboard:', error);
      
      // Manejo de errores específicos
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        // No hacer nada aquí ya que el ApiService ya maneja la redirección automática
        throw error; // Re-lanzar el error para que el componente pueda manejarlo si es necesario
      } else if (error.message.includes('403')) {
        throw new Error('Acceso denegado. Contacta al administrador del sistema');
      } else if (error.message.includes('404')) {
        throw new Error('Endpoint de estadísticas no encontrado');
      } else if (error.message.includes('500')) {
        throw new Error('Error del servidor al obtener estadísticas');
      } else if (error.message.includes('Network')) {
        throw new Error('Error de conexión. Verifica tu conexión a internet');
      }
      
      throw error;
    }
  }

  // Formatear datos para gráficos
  formatearDatosParaGraficos(estadisticas) {
    try {
      // Datos paral gráfico de actividad semanal
      const actividadSemanal = estadisticas.actividadPorDia?.map(dia => ({
        fecha: new Date(dia.fecha).toLocaleDateString('es-AR', { 
          day: '2-digit', 
          month: '2-digit' 
        }),
        threads: dia.threadsCreados,
        fechaCompleta: dia.fecha
      })) || [];

      // Datos para gráfico de uso por asistente (si hay datos)
      const usosPorAsistente = estadisticas.usosPorAsistente?.map(uso => ({
        nombre: uso.nombreAsistente || 'Sin nombre',
        usos: uso.totalUsos || 0,
        porcentaje: uso.porcentajeUso || 0
      })) || [];

      // Resumen de estadísticas principales
      const resumenPrincipal = {
        totalAsistentes: estadisticas.totalAsistentesAsignados || 0,
        totalThreads: estadisticas.totalThreadsCreados || 0,
        threadsHoy: estadisticas.threadsActivosHoy || 0,
        threadsSemana: estadisticas.threadsActivosEstaSemana || 0,
        threadsMes: estadisticas.threadsActivosEsteMes || 0,
        asistenteMasUsado: estadisticas.asistenteMasUsado || 'Ninguno'
      };


      return {
        actividadSemanal,
        usosPorAsistente,
        resumenPrincipal,
        fechaPrimerThread: estadisticas.fechaPrimerThread,
        fechaUltimoThread: estadisticas.fechaUltimoThread
      };

    } catch (error) {
      console.error('❌ Error al formatear datos para gráficos:', error);
      
      // Retornar datos por defecto en caso de error
      return {
        actividadSemanal: [],
        usosPorAsistente: [],
        resumenPrincipal: {
          totalAsistentes: 0,
          totalThreads: 0,
          threadsHoy: 0,
          threadsSemana: 0,
          threadsMes: 0,
          asistenteMasUsado: 'Ninguno'
        },
        fechaPrimerThread: null,
        fechaUltimoThread: null
      };
    }
  }

  // Calcular tendencias y métricas adicionales
  calcularTendencias(datosFormateados) {
    try {
      const { actividadSemanal, resumenPrincipal } = datosFormateados;
      
      // Calcular promedio diario de la semana
      const totalThreadsSemana = actividadSemanal.reduce((sum, dia) => sum + dia.threads, 0);
      const promedioDiario = actividadSemanal.length > 0 ? 
        Math.round((totalThreadsSemana / actividadSemanal.length) * 100) / 100 : 0;

      // Encontrar el día más activo
      const diaMasActivo = actividadSemanal.reduce((max, dia) => 
        dia.threads > max.threads ? dia : max, 
        { fecha: 'Ninguno', threads: 0 }
      );

      // Calcular tasa de crecimiento (threads este mes vs semana)
      const tasaCrecimiento = resumenPrincipal.threadsSemana > 0 ? 
        Math.round(((resumenPrincipal.threadsMes - resumenPrincipal.threadsSemana) / 
        resumenPrincipal.threadsSemana) * 100) : 0;


      return {
        promedioDiario,
        diaMasActivo,
        tasaCrecimiento,
        totalThreadsSemana
      };

    } catch (error) {
      console.error('❌ Error al calcular tendencias:', error);
      return {
        promedioDiario: 0,
        diaMasActivo: { fecha: 'Ninguno', threads: 0 },
        tasaCrecimiento: 0,
        totalThreadsSemana: 0
      };
    }
  }
}

// Crear instancia singleton
const dashboardService = new DashboardService();

export default dashboardService;
